// Persistent, resumable, cancellable bulk-job engine. Jobs are stored per-user
// in localStorage so they survive page refresh / navigation and resume on load.
// Events are written in configurable batches; progress is persisted after every
// batch so processing recovers safely after an interruption. All AI events are
// tagged with the job's id for isolated rollback, and use a deterministic
// sourceEventId for idempotency (no duplicates on retry/restart).

import { scopedKey, getActiveUid } from "./userScope";
import { bulkCreateEvents, deleteEventsByBulkJob } from "./storage";
import { BULK_CONFIG } from "./bulkConfig";
import { makeId } from "./bulkProposals";

const JOBS_KEY = () => scopedKey("bulkJobs.v1");

// In-memory guard so a job is not run twice concurrently in the same tab.
const running = new Set();

function readJobs() {
  try {
    const raw = localStorage.getItem(JOBS_KEY());
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function writeJobs(jobs) {
  localStorage.setItem(JOBS_KEY(), JSON.stringify(jobs));
  window.dispatchEvent(new CustomEvent("chronicles:bulkjobs-changed"));
}

export function getBulkJobs() {
  return readJobs().sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
}
export function getBulkJob(jobId) {
  return readJobs().find((j) => j.jobId === jobId) || null;
}
function upsertJob(job) {
  const jobs = readJobs();
  const i = jobs.findIndex((j) => j.jobId === job.jobId);
  if (i === -1) jobs.push(job);
  else jobs[i] = job;
  writeJobs(jobs);
  return job;
}
function patchJob(jobId, patch) {
  const job = getBulkJob(jobId);
  if (!job) return null;
  const next = { ...job, ...(typeof patch === "function" ? patch(job) : patch) };
  return upsertJob(next);
}

const ACTIVE = new Set(["queued", "validating", "processing", "cancelling", "rolling_back"]);

// Create a queued job from a validated bulk proposal. Returns { ok, jobId, reason }.
export function createBulkJob(proposal, config = BULK_CONFIG) {
  if (!proposal || proposal.kind !== "bulk_create_events") {
    return { ok: false, reason: "not a bulk proposal" };
  }
  if (!proposal.validationStatus || !proposal.validationStatus.valid) {
    return { ok: false, reason: "proposal failed validation" };
  }
  const active = readJobs().filter((j) => ACTIVE.has(j.status)).length;
  if (active >= config.maxConcurrentJobsPerUser) {
    return { ok: false, reason: "another bulk job is already running" };
  }
  if (proposal.events.length > config.maxEventsPerJob) {
    return { ok: false, reason: "exceeds max events per job" };
  }
  const jobId = makeId("job");
  const job = {
    jobId,
    proposalId: proposal.proposalId,
    userId: getActiveUid() || null,
    title: proposal.title,
    status: "queued",
    totalCount: proposal.events.length,
    processedCount: 0,
    successCount: 0,
    failedCount: 0,
    cancelledCount: 0,
    currentBatch: 0,
    batchSize: config.batchSize,
    createdAt: new Date().toISOString(),
    startedAt: null,
    completedAt: null,
    errors: [],
    rollbackStatus: null,
    cancelRequested: false,
    // The plan: each entry carries its deterministic sourceEventId + draft + done flag.
    items: proposal.events.map((d) => ({ sourceEventId: d.sourceEventId, draft: d, done: false })),
  };
  upsertJob(job);
  return { ok: true, jobId, reason: null };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Process a job to completion. Safe to call repeatedly (resume): it continues
// from persisted progress and never double-creates events (sourceEventId dedupe).
export async function runBulkJob(jobId, { onProgress } = {}) {
  if (running.has(jobId)) return getBulkJob(jobId);
  running.add(jobId);
  try {
    let job = getBulkJob(jobId);
    if (!job || !ACTIVE.has(job.status)) return job;
    if (!job.startedAt) job = patchJob(jobId, { startedAt: new Date().toISOString() });
    job = patchJob(jobId, { status: "processing" });

    const size = job.batchSize || BULK_CONFIG.batchSize;
    while (true) {
      job = getBulkJob(jobId);
      if (!job) return null;
      if (job.cancelRequested) {
        const remaining = job.items.filter((it) => !it.done).length;
        job = patchJob(jobId, { status: "cancelled", cancelledCount: remaining, completedAt: new Date().toISOString() });
        onProgress?.(job);
        return job;
      }
      const pending = job.items.filter((it) => !it.done);
      if (pending.length === 0) break;

      const batch = pending.slice(0, size);
      const drafts = batch.map((it) => ({
        ...it.draft,
        bulkJobId: jobId,
        sourceEventId: it.sourceEventId,
      }));

      let created = 0;
      let skipped = 0;
      const batchErrors = [];
      try {
        const res = bulkCreateEvents(drafts);
        created = res.created;
        skipped = res.skipped;
      } catch (e) {
        for (const it of batch) batchErrors.push({ sourceEventId: it.sourceEventId, event: it.draft, reason: String(e && e.message || e), retryStatus: "pending" });
      }

      // Mark this batch's items done (created or skipped-as-duplicate both count
      // as processed; only a thrown batch error leaves them for retry).
      const doneSids = new Set(batch.map((it) => it.sourceEventId));
      const errored = batchErrors.length > 0;
      job = patchJob(jobId, (j) => ({
        items: j.items.map((it) => (doneSids.has(it.sourceEventId) && !errored ? { ...it, done: true } : it)),
        processedCount: j.processedCount + (errored ? 0 : batch.length),
        successCount: j.successCount + created,
        failedCount: j.failedCount + batchErrors.length,
        currentBatch: j.currentBatch + 1,
        errors: [...j.errors, ...batchErrors],
      }));
      onProgress?.(job);
      await sleep(0); // yield to the UI; also the resume/refresh checkpoint
    }

    job = getBulkJob(jobId);
    const finalStatus = job.failedCount > 0 ? "partially_failed" : "completed";
    job = patchJob(jobId, { status: finalStatus, completedAt: new Date().toISOString() });
    onProgress?.(job);
    return job;
  } finally {
    running.delete(jobId);
  }
}

export function cancelBulkJob(jobId) {
  const job = getBulkJob(jobId);
  if (!job) return null;
  if (job.status === "queued") {
    return patchJob(jobId, { status: "cancelled", cancelRequested: true, cancelledCount: job.totalCount, completedAt: new Date().toISOString() });
  }
  return patchJob(jobId, { cancelRequested: true, status: "cancelling" });
}

// Retry only the failed items of a job (they were never created, so re-running
// with the same sourceEventId is safe and idempotent).
export async function retryFailedItems(jobId, opts = {}) {
  const job = getBulkJob(jobId);
  if (!job) return null;
  const failedSids = new Set(job.errors.map((e) => e.sourceEventId));
  if (failedSids.size === 0) return job;
  patchJob(jobId, (j) => ({
    status: "processing",
    cancelRequested: false,
    failedCount: 0,
    errors: [],
    completedAt: null,
    items: j.items.map((it) => (failedSids.has(it.sourceEventId) ? { ...it, done: false } : it)),
  }));
  return runBulkJob(jobId, opts);
}

// Undo a completed / partially-completed job: remove ONLY its events.
export function rollbackBulkJob(jobId) {
  const job = getBulkJob(jobId);
  if (!job) return null;
  patchJob(jobId, { rollbackStatus: "rolling_back", status: "rolling_back" });
  const { removed } = deleteEventsByBulkJob(jobId);
  return patchJob(jobId, { rollbackStatus: "rolled_back", status: "rolled_back", rolledBackCount: removed });
}

export function removeBulkJob(jobId) {
  writeJobs(readJobs().filter((j) => j.jobId !== jobId));
}

// Finished bulk-job cards should not linger across sessions. Prune terminal jobs
// whose completion is older than maxAgeHours (default 12h), keeping a just-finished
// job visible while clearing stale clutter. Active jobs are never touched.
const TERMINAL = new Set(["done", "completed", "failed", "cancelled", "rolled_back"]);
export function pruneFinishedBulkJobs(maxAgeHours = 12) {
  const cutoff = Date.now() - maxAgeHours * 3600 * 1000;
  const jobs = readJobs();
  const kept = jobs.filter((j) => {
    if (!TERMINAL.has(j.status)) return true;
    const ts = Date.parse(j.completedAt || j.updatedAt || "") || 0;
    return ts >= cutoff;
  });
  if (kept.length !== jobs.length) writeJobs(kept);
  return jobs.length - kept.length;
}

// Called on app load: resume any job left mid-flight by a refresh/navigation.
export function resumeActiveJobs(opts = {}) {
  pruneFinishedBulkJobs();
  const toResume = readJobs().filter((j) => j.status === "processing" || j.status === "queued" || j.status === "cancelling");
  for (const j of toResume) runBulkJob(j.jobId, opts);
  return toResume.map((j) => j.jobId);
}
